import type {
	SettingsTemplateCreateModel,
	SettingsTemplateModel,
} from '@/shared/generated'

import { useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { PlusIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useCreateSettingsTemplateMutation } from '@/entities/settings-template'

import { Button } from '@/shared/components/ui/button'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { processingMessage } from '@/shared/tools/message'

export const useSaveAsTemplateAction = ({
	templatePayload,
}: {
	templatePayload: SettingsTemplateCreateModel
}) => {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const queryClient = useQueryClient()
	const createTemplateMutation = useCreateSettingsTemplateMutation({
		onMutate() {
			toast.loading(processingMessage(t), { id: 'saving_template' })
		},
		onSuccess() {
			queryClient.invalidateQueries({ queryKey: ['settings-templates'] })
		},
		onSettled() {
			toast.dismiss('saving_template')
		},
	})
	const handleOpenTemplate = useCallback(
		({ id }: { id: string }) => {
			showModal('SETTINGS_TEMPLATE_MODAL', {
				id,
				template_type: templatePayload.template_type,
			})
		},
		[showModal, templatePayload.template_type]
	)
	const handleSaveAsTemplate = useCallback(() => {
		ampli.settingsDropdownSaveSettingAsTemplate()
		createTemplateMutation.mutate(templatePayload, {
			onError(error) {
				toast.error(error.message || t('fatal_error_title'))
			},
			onSuccess: ({ id, name }: SettingsTemplateModel) => {
				toast.success(
					`${t('templates', { defaultValue: 'Template' })} ${t('template_saved_successfully', { defaultValue: 'was saved successfully' })}`,
					{
						description: name,
						action: (
							<Button size='sm' onClick={() => handleOpenTemplate({ id })}>
								{t('general_open', { defaultValue: 'Open' })}
							</Button>
						),
						duration: 5000,
					}
				)
			},
		})
	}, [templatePayload, createTemplateMutation, handleOpenTemplate])

	return useMemo(
		() => ({
			key: 'save_as_template',
			label: t('settings.action.save_as_template', {
				defaultValue: 'Save as template',
			}),
			icon: <PlusIcon />,
			onClick: handleSaveAsTemplate,
			testId: 'template-action-save-as-template',
		}),
		[handleSaveAsTemplate]
	)
}
